/**
 * The ONE rule for what a screen shows when it meets an internal value it has
 * no label for (punch item 5.3, docs/11 X-2).
 *
 * Every enum in this package carries a typed `Record<Enum, string>` label map
 * beside it, and an exhaustive map needs no fallback — a missing entry is a
 * compile error. Three surfaces are genuinely open-ended at RUNTIME, though,
 * and they are the ones that were leaking:
 *
 *   * `permission_keys.module` and `feature_registry.status` are `text` columns
 *     mirrored from the governed seed at deploy, so a newer seed can reach an
 *     older bundle;
 *   * `audit_log.entity_type` records whatever wrote the row, including kinds
 *     added after the bundle shipped;
 *   * a role key comes from a table, so a grant can name a role the catalog no
 *     longer lists.
 *
 * Each of those used to fall back differently: two printed the RAW value
 * (`in_progress`, `property_manager`), one opened the underscores itself, and
 * one screen stripped only the FIRST underscore with `replace('_', ' ')` — so
 * `has_access_not_listed` would have read "has access_not_listed". This is the
 * shared answer: legible, never blank, and never the stored value verbatim.
 *
 * It is deliberately NOT a general formatter. Nothing that has a label map may
 * route through here — that would erase the intentional product vocabulary
 * ("Action item" for `key_date`, "View As" for `impersonation`) by turning it
 * back into a prettied-up column value.
 */

/**
 * `in_progress` → `In progress`; `property_manager` → `Property manager`.
 *
 * @param value the internal value, which may be empty
 * @param whenEmpty what to show for a blank value — a caller-supplied phrase,
 *   because "" has no legible form and an empty label is the one outcome worse
 *   than a raw one.
 */
export function readableFallback(value: string, whenEmpty: string): string {
  const opened = value.replaceAll('_', ' ').trim();
  if (opened === '') return whenEmpty;
  return opened.charAt(0).toUpperCase() + opened.slice(1);
}
